import React from 'react';
import { Button, InputNumber, Popconfirm, Switch, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { baseUrl } from '../../api/baseUrls';

export const BANNER_TYPE = {
  PROMOTIONAL: 'promotional',
  FESTIVE: 'festive',
  EXTERNAL: 'external',
  BASKET_PROMOTIONAL: 'basket_promotional',
};

export const BANNER_TYPE_OPTIONS = [
  {
    value: BANNER_TYPE.PROMOTIONAL,
    label: 'Promotional share',
    description: 'Highlight a Pre-IPO product listing on the portal.',
  },
  {
    value: BANNER_TYPE.FESTIVE,
    label: 'Festive',
    description: 'Seasonal or festive banner; product link is optional.',
  },
  {
    value: BANNER_TYPE.EXTERNAL,
    label: 'External',
    description: 'Redirect users to an external URL (webinar, campaign, etc.).',
  },
  {
    value: BANNER_TYPE.BASKET_PROMOTIONAL,
    label: 'Basket promotional',
    description: 'Promote a basket on the partner portal.',
  },
];

export const PORTAL_SCOPE = {
  INVESTOR: 'INVESTOR',
  PARTNER: 'PARTNER',
  ALL: 'ALL',
};

export const PORTAL_SCOPE_OPTIONS = [
  { value: PORTAL_SCOPE.INVESTOR, label: 'Investor' },
  { value: PORTAL_SCOPE.PARTNER, label: 'Partner' },
  { value: PORTAL_SCOPE.ALL, label: 'Both' },
];

export const BANNER_TYPE_LABELS = {
  [BANNER_TYPE.PROMOTIONAL]: 'Promotional',
  [BANNER_TYPE.FESTIVE]: 'Festive',
  [BANNER_TYPE.EXTERNAL]: 'External',
  [BANNER_TYPE.BASKET_PROMOTIONAL]: 'Basket promotional',
};

export const BANNER_TYPE_COLORS = {
  [BANNER_TYPE.PROMOTIONAL]: 'blue',
  [BANNER_TYPE.FESTIVE]: 'orange',
  [BANNER_TYPE.EXTERNAL]: 'purple',
  [BANNER_TYPE.BASKET_PROMOTIONAL]: 'green',
};

export const PORTAL_SCOPE_LABELS = {
  [PORTAL_SCOPE.INVESTOR]: 'Investor',
  [PORTAL_SCOPE.PARTNER]: 'Partner',
  [PORTAL_SCOPE.ALL]: 'All',
  APP_INVESTOR: 'App Investor',
  APP_PARTNER: 'App Partner',
};

export function resolveBannerImageUrl(path) {
  if (!path) return '';
  const trimmed = String(path).trim();
  if (!trimmed) return '';
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }
  const base = String(baseUrl).replace(/\/+$/, '');
  return `${base}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

export function formatBannerDate(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY');
}

export function getLinkedEntityLabel(banner) {
  if (!banner) return '—';
  if (banner.banner_type === BANNER_TYPE.EXTERNAL) {
    return banner.redirect_url || '—';
  }
  if (banner.banner_type === BANNER_TYPE.BASKET_PROMOTIONAL) {
    return banner.basket_uuid || banner.basket_id || '—';
  }
  if (banner.product_uuid || banner.product_id) {
    return banner.product_uuid || String(banner.product_id);
  }
  return '—';
}

export function buildCreatePayload(formValues, imageUrls) {
  const bannerType = formValues.banner_type;
  const name = String(formValues.name || '').trim();
  const portalScope = formValues.portal_scope;

  const payload = {
    name,
    banner_type: bannerType,
    portal_scope: portalScope,
    banner_image_url_web: imageUrls.web,
    banner_image_url_mobile: imageUrls.mobile,
  };

  if (bannerType === BANNER_TYPE.PROMOTIONAL) {
    payload.product_id = formValues.product_id;
  }

  if (bannerType === BANNER_TYPE.FESTIVE && formValues.product_id) {
    payload.product_id = formValues.product_id;
  }

  if (bannerType === BANNER_TYPE.EXTERNAL) {
    payload.redirect_url = String(formValues.redirect_url || '').trim();
  }

  if (bannerType === BANNER_TYPE.BASKET_PROMOTIONAL) {
    payload.basket_id = formValues.basket_id;
  }

  return payload;
}

export function buildProductSelectOptions(products) {
  return (products || [])
    .map((p) => ({
      value: p.uuid || p.id,
      label: `${p.short_name || p.name || 'Product'}${p.name && p.short_name ? ` — ${p.name}` : ''}`,
      product: p,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function extractBasketsFromListResponse(data) {
  const payload = data?.data ?? data;
  const raw = payload?.baskets ?? payload?.items ?? [];
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => entry?.basket ?? entry);
}

export function buildBasketSelectOptions(baskets) {
  return (baskets || [])
    .map((b) => ({
      value: b.uuid || b.id,
      label: b.name || b.uuid || String(b.id),
      basket: b,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function getBannerListColumns({
  canEdit,
  canDelete,
  onUpdateField,
  onDelete,
  updatingId,
  deletingId,
}) {
  const columns = [
    {
      title: 'Preview',
      key: 'preview',
      width: 72,
      render: (_, row) => {
        const src = resolveBannerImageUrl(row.banner_image_url_web || row.banner_image_url_mobile);
        if (!src) return '—';
        return (
          <img
            src={src}
            alt=""
            className="h-10 w-16 rounded object-cover border border-gray-100"
          />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-medium text-gray-800">{text || '—'}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'banner_type',
      key: 'banner_type',
      width: 140,
      render: (type) => (
        <Tag color={BANNER_TYPE_COLORS[type] || 'default'}>
          {BANNER_TYPE_LABELS[type] || type || '—'}
        </Tag>
      ),
    },
    {
      title: 'Portal',
      dataIndex: 'portal_scope',
      key: 'portal_scope',
      width: 100,
      render: (scope) => PORTAL_SCOPE_LABELS[scope] || scope || '—',
    },
    {
      title: 'Linked to',
      key: 'linked',
      ellipsis: true,
      render: (_, row) => (
        <span className="text-sm text-gray-600">{getLinkedEntityLabel(row)}</span>
      ),
    },
    {
      title: 'Order',
      dataIndex: 'display_order',
      key: 'display_order',
      width: 90,
      render: (value, row) =>
        canEdit ? (
          <InputNumber
            key={`order-${row.id}-${value}`}
            min={0}
            size="small"
            defaultValue={value ?? 0}
            disabled={updatingId === row.id}
            onBlur={(e) => {
              const next = Number(e.target.value);
              if (Number.isNaN(next) || next === value) return;
              onUpdateField(row, { display_order: next });
            }}
            onPressEnter={(e) => {
              const next = Number(e.target.value);
              if (Number.isNaN(next) || next === value) return;
              onUpdateField(row, { display_order: next });
            }}
          />
        ) : (
          value ?? 0
        ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active, row) =>
        canEdit ? (
          <Switch
            size="small"
            checked={Boolean(active)}
            loading={updatingId === row.id}
            onChange={(checked) => onUpdateField(row, { is_active: checked })}
          />
        ) : (
          <Tag color={active ? 'green' : 'default'}>{active ? 'Yes' : 'No'}</Tag>
        ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      width: 180,
      render: (_, row) => {
        if (!row.starts_at && !row.ends_at) return '—';
        return (
          <span className="text-xs text-gray-500">
            {formatBannerDate(row.starts_at)} – {formatBannerDate(row.ends_at)}
          </span>
        );
      },
    },
  ];

  if (canDelete) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, row) => (
        <Popconfirm
          title="Delete this banner?"
          description="This action cannot be undone."
          onConfirm={() => onDelete(row)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={deletingId === row.id}
          />
        </Popconfirm>
      ),
    });
  }

  return columns;
}
